import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { AppDispatch } from '..';
import { ArticoloMagazzino_interface } from '../../interface/ArticoloMagazzino_interface';
import {
  codiciTipoMagazzino,
  magazziniLogiciConst,
} from '../../utility_objects/const/magazzinoConst';
import {
  Articoli_fil_table_interface,
  Articoli_mag_table_interface,
} from '@bludata/common-interfaces';
import { RicercaArticoloFiltered_interface } from '../../magazzino/anagrafica/interfaces/RicercaArticoloFiltered_interface';
import { errorDialogDefaultFunction } from '@bludata/utility-functions';
import RoutingTree from '@focus.cloud/src/RoutingTree';

export interface MagazzinoSliceState {
  articolo: ArticoloMagazzino_interface | null;
  articoloEdit: ArticoloMagazzino_interface | null;
  articoliFilEdit: Articoli_fil_table_interface | null;
  articoliMagEdit: Articoli_mag_table_interface | null;
  articoloPage: number;
  loadingMagazzino: boolean;
  magazzinoSelected: number;
  filialeMagSelected: string;
  magazzinoLogicoSelected: string;
  causaliMovimentiList: string[];
  ricercaArticoloFiltered: RicercaArticoloFiltered_interface | null;
}

const initialState: MagazzinoSliceState = {
  articolo: null,
  articoloEdit: null,
  articoliFilEdit: null,
  articoliMagEdit: null,
  articoloPage: 0,
  loadingMagazzino: false,
  magazzinoSelected: codiciTipoMagazzino.MONTATURE,
  filialeMagSelected: '',
  magazzinoLogicoSelected: magazziniLogiciConst.STOCK,
  causaliMovimentiList: [],
  ricercaArticoloFiltered: {
    linea: null,
    modello: null,
    codiceTipoLenti: null,
    colore: null,
    calibro: null,
    ponte: null,
    codiceFornitore: null,
    magazzino: null,
    codiceMarchio: null,
    codiceMateriale: null,
    sfera: null,
    cilindro: null,
    asse: null,
    diametro: null,
    rb: null,
    codiceTipoProdotto: null,
  },
};

const MagazzinoSlice = createSlice({
  name: 'magazzino',
  initialState,
  reducers: {
    setArticolo(
      state: MagazzinoSliceState,
      action: PayloadAction<ArticoloMagazzino_interface>,
    ) {
      state.articolo = action.payload;
    },
    setArticoloEdit(
      state: MagazzinoSliceState,
      action: PayloadAction<ArticoloMagazzino_interface>,
    ) {
      state.articoloEdit = action.payload;
    },
    setArticoliFilEdit(
      state: MagazzinoSliceState,
      action: PayloadAction<Articoli_fil_table_interface>,
    ) {
      state.articoliFilEdit = action.payload;
    },
    setArticoliMagEdit(
      state: MagazzinoSliceState,
      action: PayloadAction<Articoli_mag_table_interface>,
    ) {
      state.articoliMagEdit = action.payload;
    },
    setArticoloPage(state: MagazzinoSliceState, action: PayloadAction<number>) {
      state.articoloPage = action.payload;
    },
    nextPageMagazzino(state: MagazzinoSliceState) {
      state.articoloPage = state.articoloPage + 1;
      state.articolo = null;
    },
    priorPageMagazzino(state: MagazzinoSliceState) {
      if (state.articoloPage > 0) {
        state.articoloPage = state.articoloPage - 1;
        state.articolo = null;
      }
    },
    setLoadingMagazzino(
      state: MagazzinoSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.loadingMagazzino = action.payload;
    },
    setMagazzinoSelected(
      state: MagazzinoSliceState,
      action: PayloadAction<number>,
    ) {
      state.magazzinoSelected = action.payload;
    },
    setFilialeMagSelected(
      state: MagazzinoSliceState,
      action: PayloadAction<string>,
    ) {
      state.filialeMagSelected = action.payload;
    },
    setMagazzinoLogicoSelected(
      state: MagazzinoSliceState,
      action: PayloadAction<string>,
    ) {
      state.magazzinoLogicoSelected = action.payload;
    },
    setCausaliMovimentiList(
      state: MagazzinoSliceState,
      action: PayloadAction<string[]>,
    ) {
      state.causaliMovimentiList = action.payload;
    },
    setRicercaArticoloFiltered(
      state: MagazzinoSliceState,
      action: PayloadAction<RicercaArticoloFiltered_interface>,
    ) {
      if (action.payload) {
        // Create a new object with only properties that have meaningful values
        let queryFilterClean = Object.entries(action.payload).reduce(
          (acc, [key, value]) => {
            // Check if the value is not null, not undefined, not an empty string, and not NaN
            if (
              value !== null &&
              value !== undefined &&
              value !== '' &&
              !Number.isNaN(value)
            ) {
              acc[key] = value;
            }
            return acc;
          },
          {},
        );
        state.ricercaArticoloFiltered = queryFilterClean;
      } else {
        state.ricercaArticoloFiltered = null;
      }
    },
    resetArticoloEdit(state: MagazzinoSliceState) {
      state.articoloEdit = initialState.articoloEdit;
      state.articoliFilEdit = initialState.articoliFilEdit;
      state.articoliMagEdit = initialState.articoliMagEdit;
    },
    resetRicercaArticoloFiltered(state: MagazzinoSliceState) {
      state.ricercaArticoloFiltered = initialState.ricercaArticoloFiltered;
      state.articoloPage = initialState.articoloPage;
    },
  },
});

export const fetchArticoloMagazzino = (
  filiale: string,
  page?: number,
  codiceFiliale?: string,
  magazzino?: number,
  barcode?: string,
  filtri?: RicercaArticoloFiltered_interface,
  magazzinoSelected?: number,
) => {
  return async (dispatch: AppDispatch) => {
    const loadArticolo = () => {
      axios
        .get(`/focusapi/magazzino/getitems`, {
          params: {
            _dc: new Date().getTime(),
            filiale,
            page: 1,
            start: 0,
            limit: 1,
            filter: JSON.stringify([
              {
                property: barcode ? 'codiceABarre' : 'codiceFiliale',
                operator: '=',
                value: barcode ? barcode : valueCodFiliale,
              },
            ]),
          },
        })
        .then((response) => {
          if (response.data && response.data.total > 0) {
            const articolo = response.data.data[0];

            if (
              (magazzinoSelected || magazzinoSelected === 0) &&
              magazzinoSelected !== articolo.magazzino
            ) {
              //!se l'articolo non è del magazzino selezionato mostro errore
              errorDialogDefaultFunction(
                "L'articolo ricercato non è all'interno del magazzino selezionato.",
              );
              return;
            }
            dispatch(setArticolo(articolo));
            //update di articoli mag in caso di refresh
            window.dispatchEvent(
              new CustomEvent('magazzino_updateArticoloMag'),
            );
          } else {
            errorDialogDefaultFunction(
              `L'articolo non è presente in magazzino.`,
            );
          }
        })
        .finally(() => {
          dispatch(setLoadingMagazzino(false));
        });
    };

    let valueCodFiliale = codiceFiliale;
    const valuePage = page || 0;
    if (!valueCodFiliale && !barcode) {
      const valueMagazzino = magazzino >= 0 ? magazzino : -2;
      //Chiedo al server l'articolo in base alla pagina in cui sono posizionato

      const query = filtri
        ? { ...filtri, magazzino: valueMagazzino }
        : { magazzino: valueMagazzino };
      axios
        .get(`/focusapi/magazzino/getlistpos`, {
          params: {
            _dc: new Date().getTime(),
            query: JSON.stringify(query),
            page: 1 + valuePage,
            start: 0 + valuePage,
            limit: 1,
          },
        })
        .then((response) => {
          if (response.data.success === false) {
            errorDialogDefaultFunction(
              `Errore durante il caricamento dell'articolo.`,
            );
            return;
          }
          if (response.data && response.data.data.length > 0) {
            valueCodFiliale = response.data.data[0]?.codiceFiliale;
            if (valueCodFiliale) {
              loadArticolo();
            }
          }
        })

        .finally(() => {
          dispatch(setLoadingMagazzino(false));
        });
    } else if (barcode) {
      loadArticolo();
    } else {
      loadArticolo();
    }

    dispatch(setLoadingMagazzino(true));
  };
};

export const {
  setArticolo,
  setArticoloEdit,
  setArticoliFilEdit,
  setArticoliMagEdit,
  setArticoloPage,
  setLoadingMagazzino,
  setMagazzinoSelected,
  nextPageMagazzino,
  priorPageMagazzino,
  setFilialeMagSelected,
  setMagazzinoLogicoSelected,
  setCausaliMovimentiList,
  resetArticoloEdit,
  setRicercaArticoloFiltered,
  resetRicercaArticoloFiltered,
} = MagazzinoSlice.actions;

export default MagazzinoSlice.reducer;
